import { caughtErrorLogFields } from "@pandas/http-client";
import { LogLevel, TimeLogger } from "@pandas/logger";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { resolveRealtimeScreening } from "@/components/image-capture/screening/resolveRealtimeScreening";
import { createScreeningCaptureLog } from "@/components/image-capture/screening/screeningLog";
import { getMediaStream } from "@/components/phone-capture/capture-wizard/cameraController";
import { getExpectedAngles } from "@/components/phone-capture/capture-wizard/flows/angleFlowFactory";
import { getCaptureFlowConfig } from "@/components/phone-capture/capture-wizard/flows/captureFlowFactory";
import { IncompleteUploadError } from "@/components/phone-capture/capture-wizard/phoneAngleWizard";
import useDarkMode from "@/hooks/useDarkMode";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { publishWithTimeout, useVisionChannel } from "@/lib/ably/vision";
import { type CameraProvider, getCameraProvider } from "@/lib/camera";
import { getLogger } from "@/lib/logging/logger";
import { imageCaptureSearchParamsSchema } from "@/routes/-search-params";
import type { GradingPageConfig } from "@/services/types";
import { useStore } from "@/store";
import type { PhotoSet } from "@/store/slices/sessionInfo";
import { isAbortError } from "@/utils/helpers";

const logger = getLogger();

export const Route = createFileRoute("/$locale/$partner/_app/_card/_paired/image-capture")({
	validateSearch: (search) => imageCaptureSearchParamsSchema.parse(search),
	component: CameraPage,
});

type CameraPageContentProps = {
	retryAngles: string[];
};

function CameraPage() {
	const { "retry-angle": retryAngle } = Route.useSearch();
	const retryAngles = useMemo(
		() => (Array.isArray(retryAngle) ? retryAngle : retryAngle ? [retryAngle] : []),
		[retryAngle],
	);

	return <CameraPageContent retryAngles={retryAngles} />;
}

function CameraPageContent({ retryAngles }: CameraPageContentProps) {
	const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const { goToNextStep } = useNavigationHelpers();
	const sessionId = useStore((state) => state.sessionId) as string;
	const config = useStore((state) => state.globalConfig?.grading) as GradingPageConfig;
	const captureFlowId =
		useStore((state) => state.config?.pagesConfig.imageCapture?.captureFlow) || "BACK_CAMERA_MANUAL";
	const setPhotos = useStore((state) => state.setPhotos);
	const setError = useStore((state) => state.setError);
	const clearCapturedAngles = useStore((state) => state.clearCapturedAngles);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const cameraResolution = useStore((state) => state.globalConfig?.cameraResolution);
	const captureAnglesFromConfig = useStore((state) => state.config?.pagesConfig.imageCapture.captureAngles);
	const captureAngles = useMemo(() => captureAnglesFromConfig ?? [], [captureAnglesFromConfig]);
	const timeLoggerRef = useRef<TimeLogger | null>(null);
	const isMountedRef = useRef(true);
	const { cameraFacingMode, angleSequence, coordinatorClass, Component, cameraFeed, nativeCamera } =
		getCaptureFlowConfig(captureFlowId);
	// A flow without a native path keeps the browser camera even inside the wrapper, which grants
	// getUserMedia whenever its own camera is not running.
	const cameraProvider: CameraProvider = nativeCamera && getCameraProvider() === "native" ? "native" : "browser";
	const realtimeScreeningFlag = useStore((state) => state.config?.pagesConfig.imageCapture?.realtimeScreening);
	const screeningFallback = useStore((state) => state.screeningFallback);
	const screening = resolveRealtimeScreening({
		captureFlowId,
		cameraFeed,
		cameraProvider,
		realtimeScreening: realtimeScreeningFlag,
		runtimeFallback: screeningFallback,
	});
	const { channel: ablyChannel } = useVisionChannel(sessionId);

	useDarkMode(cameraFeed);

	const getCameraFeed = useEffectEvent(async () => {
		getMediaStream(cameraFacingMode, false, cameraResolution)
			.then((stream) => {
				setCameraStream(stream);
				// mirror the video
				if (!videoRef.current) throw "Not video ref found";
				videoRef.current.srcObject = stream;

				// Wait for video to be ready before starting animation
				videoRef.current.onloadedmetadata = () => {
					videoRef.current?.play();
				};
			})
			.catch((error) => {
				logger.warning("FLOW", "Could not get camera feed", caughtErrorLogFields(error));
				setCameraError(error);
			});
	});

	// Must be stable: the capture components pass this to the wizard `useMemo` deps, and a
	// reference flip mid-upload (e.g. Ably channel ref swapping on peer presence churn) would
	// recreate the wizard and abort in-flight uploads via the cleanup.
	const onAllImagesCaptured = useEffectEvent(async (uploadImages: () => Promise<PhotoSet>) => {
		timeLoggerRef.current?.StopAndLogMessage(LogLevel.info, "capture-flow-" + captureFlowId, "Capture flow duration", {
			bidirectional: config.bidirectionalFlow,
		});

		let imageUrls: PhotoSet;
		try {
			imageUrls = await uploadImages();
		} catch (error) {
			if (error instanceof IncompleteUploadError) {
				// The wizard logged each failed upload; its metrics line carries the abandoned ids.
				setError(error);
				return;
			}
			if (isAbortError(error)) {
				// Defensive: the wizard should now always surface aborts as IncompleteUploadError
				// (after its one-shot auto-retry). If a raw AbortError still escapes, surface it
				// rather than silently leaving the user stranded on the capture screen.
				logger.warning("FLOW", "Image upload aborted without recovery", caughtErrorLogFields(error));
				setError(error as Error);
				return;
			}
			logger.warning("FLOW", "Image upload failed", caughtErrorLogFields(error));
			setError(error as Error);
			return;
		}

		if (!isMountedRef.current) return;

		try {
			await publishWithTimeout(ablyChannel, "session-status-update", {
				client: "scanner-device",
				status: "image-capture-completed",
			});
		} catch (error) {
			logger.warning("FLOW", "Could not publish image-capture-completed status", caughtErrorLogFields(error));
		}

		setPhotos(imageUrls);
		clearCapturedAngles();
		goToNextStep();
	});

	const publishStartedStatus = useEffectEvent(async () => {
		try {
			await publishWithTimeout(ablyChannel, "session-status-update", {
				client: "scanner-device",
				status: "image-capture-started",
			});
		} catch (error) {
			logger.warning("FLOW", "Could not publish image-capture-started status", caughtErrorLogFields(error));
		}
	});

	useEffect(() => {
		isMountedRef.current = true;
		if (!timeLoggerRef.current) {
			timeLoggerRef.current = new TimeLogger(logger);
			timeLoggerRef.current.Start();
		}

		// Stale-state recovery: if `capturedAngles` claims every expected angle is captured but
		// the photo store doesn't have URLs for all of them, the prior session lost its uploads
		// (page reload mid-upload, or sessionStorage carries `capturedAngles` from a flow that
		// never reached `setPhotos`). Wipe the stale claim so the wizard hydrates as a fresh
		// start instead of short-circuiting completion with no photos.
		const { capturedAngles, photos } = useStore.getState();
		const expectedAngles = getExpectedAngles(angleSequence, captureAngles);
		const allClaimedCaptured = expectedAngles.every((angle) => capturedAngles.includes(angle));
		const allPhotosPresent = photos !== null && expectedAngles.every((angle) => photos[angle]);
		if (allClaimedCaptured && !allPhotosPresent) {
			clearCapturedAngles();
		}

		return () => {
			isMountedRef.current = false;
		};
		// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only effect
	}, []);

	useEffect(() => {
		if (screening.skippedReason !== "native-camera") return;
		const skipped = createScreeningCaptureLog().screeningSkipped("native-camera");
		logger.info("INFO", skipped.event, skipped.fields);
	}, [screening.skippedReason]);

	useEffect(() => {
		if (!ablyChannel) return;

		publishStartedStatus();
	}, [ablyChannel]);

	useEffect(() => {
		if (!cameraFeed || cameraProvider === "native" || screening.isActive) return;

		const timeoutId = window.setTimeout(() => {
			getCameraFeed();
		}, 200);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [cameraFeed, cameraProvider, screening.isActive]);

	useEffect(() => {
		return () => {
			if (cameraStream) {
				cameraStream.getTracks().forEach((track) => track.stop());
			}
		};
	}, [cameraStream]);

	return (
		<>
			{cameraError && (
				<button
					className="bg-zinc-500 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-sm"
					onClick={getCameraFeed}
				>
					Retry
				</button>
			)}
			{
				<Component
					onAllImagesCaptured={onAllImagesCaptured}
					cameraStream={cameraStream}
					canvasRef={canvasRef}
					videoRef={videoRef}
					sessionId={sessionId}
					config={config}
					coordinatorClass={coordinatorClass}
					angleSequence={angleSequence}
					cameraFacingMode={cameraFacingMode}
					cameraProvider={cameraProvider}
					retryAngles={retryAngles}
					captureAngles={captureAngles}
					realtimeScreening={screening.isActive}
				/>
			}
		</>
	);
}
